import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * `when` = where the section top must sit in the viewport before reveal (0–1).
 *   0    → top of the viewport (latest)
 *   0.9  → 90% down (default) — just inside the viewport
 *   1    → bottom of the viewport (fires as soon as it enters)
 */
const defaults = {
  when: 0.9,
  once: true,
  className: "is-in-view",
};

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function whenToStart(when) {
  if (when <= 0) {
    return "top top";
  }

  if (when >= 1) {
    return "top bottom";
  }

  return `top ${Math.round(when * 100)}%`;
}

function hasRequiredContent(section, require) {
  if (!require) {
    return true;
  }

  return Boolean(section.querySelector(require));
}

function revealSection(section, className) {
  if (section.classList.contains(className)) {
    return;
  }

  section.classList.add(className);
}

function scheduleReveal(section, className) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      revealSection(section, className);
    });
  });
}

function revealActiveTriggers(triggers) {
  triggers.forEach(({ section, className, trigger }) => {
    if (trigger.isActive || isInViewport(section)) {
      scheduleReveal(section, className);
    }
  });
}

function isInViewport(section) {
  const rect = section.getBoundingClientRect();

  return rect.bottom > 0 && rect.top < window.innerHeight;
}

function initScrollRevealBlock(block) {
  const className = block.className ?? defaults.className;
  const start = whenToStart(block.when ?? defaults.when);
  const sections = document.querySelectorAll(block.selector);
  const triggers = [];

  sections.forEach((section) => {
    if (!section.closest(".has-animation")) {
      return;
    }

    if (!hasRequiredContent(section, block.require)) {
      return;
    }

    if (reducedMotion) {
      revealSection(section, className);
      return;
    }

    const trigger = ScrollTrigger.create({
      trigger: section,
      start,
      once: block.once ?? defaults.once,
      onEnter: () => {
        revealSection(section, className);
      },
    });

    triggers.push({ section, className, trigger });

    if (trigger.isActive || isInViewport(section)) {
      scheduleReveal(section, className);
    }
  });

  return triggers;
}

export function initScrollReveal(blocks) {
  const triggers = [];

  blocks.forEach((block) => {
    triggers.push(...(initScrollRevealBlock(block) ?? []));
  });

  window.addEventListener(
    "load",
    () => {
      ScrollTrigger.refresh();
      revealActiveTriggers(triggers);
    },
    { once: true },
  );
}

const scrollRevealBlocks = [
  {
    selector: ".column-split",
    require: ".column-split__primary, .column-split__secondary",
  },
  {
    selector: ".boats",
    require: ".boats__headline, .card-slider, .boats__grid",
  },
  {
    selector: ".text-section",
    require: ".text-section__headline, .text-section__block",
  },
  {
    selector: ".text-boxes",
    require: ".page-section__headline, .text-boxes__item",
  },
  {
    selector: ".media-text",
    require: ".media-text__headline, .media-text__content, .media-text__media",
  },
  {
    selector: ".gallery",
    require: ".gallery__headline, .gallery__item",
  },
  {
    selector: ".video-gallery",
    require: ".video-gallery__headline, .video-gallery__item",
  },
  {
    selector: ".contest-gallery",
    require: ".contest-gallery__headline, .contest-gallery__item",
  },
  {
    selector: ".boat-locations-table",
    require: ".page-section__headline, .table-wrapper",
  },
  {
    selector: ".boat-comparison-table",
    require: ".page-section__headline, .table-wrapper",
  },
  {
    selector: ".price-overview-tables",
    require: ".price-overview-tables__group",
  },
  {
    selector: ".data-table",
    require: ".data-table__table",
  },
  {
    selector: ".boat-sleeping-places",
    require: ".boat-sleeping-places__group",
  },
  {
    selector: ".teaser-list",
    require: ".teaser-list__headline, .teaser-list__item",
  },
  {
    selector: ".image-card-slider",
    require: ".image-card-slider__headline, .image-card-slider__slide",
  },
  {
    selector: ".faq-section",
    require: ".faq-section__headline, .accordion-item",
  },
  {
    selector: ".faq-list",
    require: ".faq-list__header",
  },
  {
    selector: ".job-list",
    require: ".job-list__headline, .job-list__stack",
  },
  {
    selector: ".icon-grid",
    require: ".icon-grid__item",
  },
  {
    selector: ".separator",
    require: ".separator__line",
  },
  {
    selector: ".testimonials",
    require: ".testimonials__headline, .testimonials__item",
  },
  {
    selector: ".facts",
    require: ".facts__item",
  },
  {
    selector: ".blog-posts",
    require: ".blog-posts__headline, .blog-posts__grid",
  },
  {
    selector: ".press-publications",
    require: ".page-section__headline, .accordion-group",
  },
  {
    selector: ".press-profile",
    require:
      ".page-section__headline, .press-profile__list, .press-profile__action",
  },
  {
    selector: ".press-logos",
    require: ".page-section__headline, .press-logos__panel",
  },
  {
    selector: ".cta-banner",
    require: ".cta-banner__intro, .cta-banner__content",
  },
];

initScrollReveal(scrollRevealBlocks);
